import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  useOrders,
  useStartUnwinder,
  useStopUnwinder,
  useUnwinderState,
  useUnwindingRules
} from '../../api/useUnwinding';

const OPEN_ORDER_STATUSES = new Set(['resting', 'live', 'partial', 'pending']);

export const StopStartUnwinderButtons = () => {
  const { data: state } = useUnwinderState();
  const { data: rules } = useUnwindingRules();
  const { data: ordersData } = useOrders();
  const stopMutation = useStopUnwinder();
  const startMutation = useStartUnwinder();

  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);

  const activeRuleIds = useMemo(
    () =>
      new Set(
        Array.isArray(rules)
          ? rules.filter(r => r.status === 'active').map(r => r._id)
          : []
      ),
    [rules]
  );
  const activeRuleCount = activeRuleIds.size;
  const pausedRuleCount = useMemo(
    () =>
      Array.isArray(rules) ? rules.filter(r => r.status === 'paused').length : 0,
    [rules]
  );
  const { openOrdersFromActiveRules, openOrdersFromPausedRules } = useMemo(() => {
    const openOrders = (ordersData?.orders ?? []).filter(o =>
      OPEN_ORDER_STATUSES.has(o.status)
    );
    return {
      openOrdersFromActiveRules: openOrders.filter(
        o => o.ruleId && activeRuleIds.has(o.ruleId)
      ).length,
      openOrdersFromPausedRules: openOrders.filter(
        o => !o.ruleId || !activeRuleIds.has(o.ruleId)
      ).length,
    };
  }, [ordersData, activeRuleIds]);

  const isRunning = state?.isRunning ?? true;
  const isBusy = stopMutation.isLoading || startMutation.isLoading;
  // Panic only fires when active rules exist. Open orders tied to paused rules
  // are intentionally left alone (user-managed), so they don't block the stop.
  const willTriggerPanic = activeRuleCount > 0;

  const handleConfirmStop = () => {
    stopMutation.mutate();
    setStopConfirmOpen(false);
  };

  const handleConfirmStart = () => {
    startMutation.mutate();
    setStartConfirmOpen(false);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip
          title={!isRunning ? 'Unwinder already stopped' : 'Stop position-unwinder instance'}
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                stopMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PowerSettingsNewIcon />
                )
              }
              onClick={() => setStopConfirmOpen(true)}
              disabled={isBusy || !isRunning}
              sx={{ fontSize: '0.7rem' }}
            >
              Stop Unwinder
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={isRunning ? 'Unwinder already running' : 'Start position-unwinder instance'}
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={
                startMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => setStartConfirmOpen(true)}
              disabled={isBusy || isRunning}
              sx={{ fontSize: '0.7rem' }}
            >
              Start Unwinder
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Stop confirmation */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => setStopConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Stop Position Unwinder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
              This scales the <code>position-unwinder</code> deployment to 0 replicas.
              It is separate from the global Predictions on/off and from PANIC.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Rules to pause:</b> {activeRuleCount}
                {pausedRuleCount > 0 && (
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: 'grey.500' }}>
                    {' '}({pausedRuleCount} already paused)
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to cancel:</b> {openOrdersFromActiveRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(from active rules)
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to remain open:</b> {openOrdersFromPausedRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(unrelated to active rules)
                </Typography>
              </Typography>
            </Box>
            {willTriggerPanic ? (
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                <b>PANIC</b> will fire to pause {activeRuleCount} rule
                {activeRuleCount === 1 ? '' : 's'} and cancel {openOrdersFromActiveRules} order
                {openOrdersFromActiveRules === 1 ? '' : 's'}, then the unwinder will stop.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                No active rules. Panic will be skipped; unwinder will stop directly.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} open order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStop}
            variant="contained"
            color="warning"
            size="small"
            disabled={stopMutation.isLoading}
          >
            {stopMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Stop Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start confirmation */}
      <Dialog
        open={startConfirmOpen}
        onClose={() => setStartConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Start Position Unwinder</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
            Scale <code>position-unwinder</code> back to 1 replica.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStart}
            variant="contained"
            color="success"
            size="small"
            disabled={startMutation.isLoading}
          >
            {startMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Start Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
